import {
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Card, Empty, Select, Tooltip } from 'antd';
import React, { useEffect, useRef } from 'react';
import { TOY_RESERVATION_RULE_TYPES } from '../common/constants';
import ReservationRuleOptionLabel from './ReservationRuleOptionLabel';

const RULE_TYPE_OPTIONS = Object.values(TOY_RESERVATION_RULE_TYPES)?.map(
  (item) => ({ label: item?.label, value: item?.value }),
);

const GROUP_HINT =
  'Rules in this group are joined with OR — the subscriber needs to match at least one of them.';

const BETWEEN_GROUPS_HINT =
  'Groups are joined with AND — the subscriber must match this group as well as every other group.';

export const emptyRuleRow = () => ({ type: null, ruleId: null });

export const emptyRuleGroups = () => [{ rules: [emptyRuleRow()] }];

export const buildGroupsFromAttachments = (attachments = []) => {
  const byGroup = new Map();
  attachments?.forEach((attachment) => {
    const key = attachment?.ruleGroup ?? 1;
    if (!byGroup?.has(key)) byGroup?.set(key, []);
    byGroup?.get(key)?.push({
      type: attachment?.rule?.type || null,
      ruleId: attachment?.toyReservationRuleId || null,
      rule: attachment?.rule || null,
    });
  });

  return [...byGroup?.keys()]
    ?.sort((a, b) => a - b)
    ?.map((key) => ({ rules: byGroup?.get(key) }));
};

export const buildAttachRulesPayload = (groups = []) => {
  const rules = [];
  groups?.forEach((group, groupIndex) => {
    group?.rules?.forEach((row) => {
      if (!row?.ruleId) return;
      // groups are numbered from 1 in the order they appear in the builder
      rules?.push({
        isActive: true,
        ruleGroup: groupIndex + 1,
        toyReservationRuleId: row?.ruleId,
      });
    });
  });
  return rules;
};

export const collectUniqueAttachedRules = (inventories = []) =>
  Array.from(
    new Map(
      inventories
        ?.flatMap((inventory) => inventory?.toyReservationRules || [])
        ?.map((attachment) => attachment?.rule)
        ?.filter((rule) => rule?.id)
        ?.map((rule) => [rule?.id, rule]),
    )?.values(),
  );

export const buildAddRulesInput = (libraryInventoryIds = [], groups = []) => ({
  isAddOperation: true,
  libraryInventoryIds,
  rules: buildAttachRulesPayload(groups),
});

export const buildRemoveRulesInput = (libraryInventoryIds = [], ruleIds = []) => ({
  isAddOperation: false,
  libraryInventoryIds,
  rules: ruleIds?.map((toyReservationRuleId) => ({ toyReservationRuleId })),
});

export const collectUsedRuleIds = (groups = []) =>
  groups
    ?.flatMap((group) => group?.rules?.map((row) => row?.ruleId))
    ?.filter(Boolean) || [];

export const isRuleGroupsInvalid = (groups = []) => {
  if (!groups?.length) return true;
  return groups?.some(
    (group) => !group?.rules?.length || group?.rules?.some((row) => !row?.ruleId),
  );
};

const ReservationRuleGroups = ({
  groups = [],
  rulesByType = {},
  rulesById = {},
  isRulesLoading = false,
  onGroupsChange,
  readOnly = false,
  emptyDescription = 'No rule groups yet',
}) => {
  const usedRuleIds = collectUsedRuleIds(groups);
  const bottomRef = useRef(null);
  const shouldScrollToBottomRef = useRef(false);

  useEffect(() => {
    if (!shouldScrollToBottomRef.current) return;
    shouldScrollToBottomRef.current = false;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [groups?.length]);

  const updateRow = (groupIndex, rowIndex, changes) => {
    onGroupsChange(
      groups?.map((group, gIndex) => {
        if (gIndex !== groupIndex) return group;
        return {
          ...group,
          rules: group?.rules?.map((row, rIndex) =>
            rIndex === rowIndex ? { ...row, ...changes } : row,
          ),
        };
      }),
    );
  };

  const addRow = (groupIndex) => {
    onGroupsChange(
      groups?.map((group, gIndex) =>
        gIndex === groupIndex
          ? { ...group, rules: [...group?.rules, emptyRuleRow()] }
          : group,
      ),
    );
  };

  const removeGroup = (groupIndex) => {
    onGroupsChange(groups?.filter((_, gIndex) => gIndex !== groupIndex));
  };

  const removeRow = (groupIndex, rowIndex) => {
    if (groups?.[groupIndex]?.rules?.length === 1) {
      removeGroup(groupIndex);
      return;
    }
    onGroupsChange(
      groups?.map((group, gIndex) =>
        gIndex === groupIndex
          ? {
              ...group,
              rules: group?.rules?.filter((_, rIndex) => rIndex !== rowIndex),
            }
          : group,
      ),
    );
  };

  const addGroup = () => {
    shouldScrollToBottomRef.current = true;
    onGroupsChange([...groups, { rules: [emptyRuleRow()] }]);
  };

  const renderReadOnlyRow = (row) => {
    const rule = row?.rule || rulesById?.[row?.ruleId];
    return (
      <div className="reservation-rule-readonly-row">
        <ReservationRuleOptionLabel
          rule={rule || { name: row?.ruleId, type: row?.type }}
        />
      </div>
    );
  };

  const renderEditableRow = (group, groupIndex, row, rowIndex) => {
    const options = (rulesByType?.[row?.type] || [])?.filter(
      (option) =>
        option?.value === row?.ruleId || !usedRuleIds?.includes(option?.value),
    );

    const attachedRule = row?.rule || rulesById?.[row?.ruleId];
    if (
      row?.ruleId &&
      !options?.some((option) => option?.value === row?.ruleId)
    ) {
      options?.unshift({
        value: row?.ruleId,
        label: attachedRule?.name || row?.ruleId,
      });
    }

    return (
      <div className="reservation-rule-row">
        <Select
          className="reservation-rule-select reservation-rule-select--type"
          placeholder="Select Rule Type"
          value={row?.type || undefined}
          options={RULE_TYPE_OPTIONS}
          onChange={(value) =>
            updateRow(groupIndex, rowIndex, { type: value, ruleId: null })
          }
        />

        <Select
          className="reservation-rule-select reservation-rule-select--rule"
          showSearch
          optionFilterProp="label"
          dropdownMatchSelectWidth={false}
          popupClassName="reservation-rule-dropdown"
          loading={isRulesLoading}
          disabled={!row?.type}
          placeholder={row?.type ? 'Select Rule' : 'Select a rule type first'}
          value={row?.ruleId || undefined}
          options={options}
          notFoundContent={isRulesLoading ? 'Loading…' : 'No rules of this type'}
          onChange={(value) => updateRow(groupIndex, rowIndex, { ruleId: value })}
        />

        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => removeRow(groupIndex, rowIndex)}
        />
      </div>
    );
  };

  if (!groups?.length && readOnly) {
    return (
      <Empty
        image={Empty?.PRESENTED_IMAGE_SIMPLE}
        description={emptyDescription}
      />
    );
  }

  return (
    <>
      {!groups?.length && (
        <Empty
          className="mb-16"
          image={Empty?.PRESENTED_IMAGE_SIMPLE}
          description={emptyDescription}
        />
      )}

      {groups?.map((group, groupIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={groupIndex}>
          {groupIndex > 0 && (
            <div className="reservation-rule-group-joiner">
              <span className="reservation-rule-joiner-line" />
              <Tooltip title={BETWEEN_GROUPS_HINT} placement="right">
                <span className="reservation-rule-joiner pointer">AND</span>
              </Tooltip>
              <span className="reservation-rule-joiner-line" />
            </div>
          )}

          <Card
            className="mb-8"
            size="small"
            title={
              <span className="d-flex align-center" style={{ gap: 6 }}>
                Group {groupIndex + 1}
                <Tooltip title={GROUP_HINT} placement="top">
                  <InfoCircleOutlined className="pointer" />
                </Tooltip>
              </span>
            }
            extra={
              readOnly ? null : (
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => removeGroup(groupIndex)}
                />
              )
            }
          >
            {group?.rules?.map((row, rowIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`${groupIndex}-${rowIndex}`}>
                {rowIndex > 0 && <div className="reservation-rule-joiner">OR</div>}
                {readOnly
                  ? renderReadOnlyRow(row)
                  : renderEditableRow(group, groupIndex, row, rowIndex)}
              </div>
            ))}

            {!readOnly && (
              <div className="d-flex justify-center mt-8">
                <Button
                  type="link"
                  className="p-0"
                  icon={<PlusOutlined />}
                  onClick={() => addRow(groupIndex)}
                >
                  Add Rule
                </Button>
              </div>
            )}
          </Card>
        </div>
      ))}

      {!readOnly && (
        <div className="d-flex justify-center mt-8 mb-16">
          <Button icon={<PlusOutlined />} onClick={addGroup}>
            Add Group
          </Button>
        </div>
      )}

      <div ref={bottomRef} />
    </>
  );
};

export default ReservationRuleGroups;
